import { useTranslation } from '@/lib/i18n';
import { labelOf, REFUND_REASON_LABELS, REFUND_STATUS_LABELS } from '@/lib/enumLabels';
import type { RefundDetail } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { formatPaymentMinorAmount } from '@/features/orders/lib/chargeMoney';
import { krBankName } from '@/lib/domain/krBanks';

export interface RefundsListProps {
  /** 결제의 청구통화(payment.currencyCode) — 행 금액 단위/표기 기준. */
  currencyCode: string;
  refunds: ReadonlyArray<RefundDetail>;
}

// [pay-currency] RefundDetail additive chargeAmount(major decimal STRING) — be-snapshot 확장
// 예정 계약. generated 타입 미반영이라 로컬 확장(없으면 minor→major 변환 폴백).
// be-payment V20 — 수기 환불(PG 미경유) 기록이면 manual 이 붙는다(PG 환불은 null).
type RefundDetailExtended = RefundDetail & {
  chargeAmount?: string | null;
  manual?: {
    method?: 'ORIGINAL_PG' | 'BANK_TRANSFER' | 'OTHER' | null;
    bankCode?: string | null;
    /** 끝 4자리만 보이는 계좌번호(예: ******1234). */
    maskedAccountNumber?: string | null;
    holderName?: string | null;
    /** 기록한 관리자. */
    processedBy?: string | null;
    /** 실제 환불 처리 일시. */
    processedAt?: string | null;
  } | null;
};

const MANUAL_METHOD_FALLBACK: Record<string, string> = { ORIGINAL_PG: '원 PG 취소', BANK_TRANSFER: '계좌이체', OTHER: '기타' };

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const RefundsList = ({ currencyCode, refunds }: RefundsListProps) => {
  const { t } = useTranslation('payments');
  if (refunds.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('payments.detail.refundsEmpty')}
      </p>
    );
  }
  return (
    <Table data-testid="payment-refunds-list">
      <TableHeader>
        <TableRow>
          <TableHead>{t('payments.detail.refundRequestedAt')}</TableHead>
          <TableHead>{t('payments.detail.refundAmount')}</TableHead>
          <TableHead>{t('payments.detail.refundReason')}</TableHead>
          <TableHead>{t('payments.detail.refundStatus')}</TableHead>
          <TableHead>{t('payments.detail.refundCompletedAt')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {refunds.map((r) => (
          <TableRow key={r.refundId} data-refund-id={r.refundId}>
            <TableCell className="tabular-nums text-[color:var(--content-default)]">
              {formatDateTime(r.requestedAt)}
            </TableCell>
            <TableCell className="text-right tabular-nums text-[color:var(--content-default)]">
              {/* [pay-currency] r.amount 는 결제 청구통화의 minor 정수. 표기 정본은 행 단위
                  chargeAmount(be-snapshot 확장), 없으면 minor→major 정확 변환 폴백.
                  ★통화는 결제의 청구통화(prop) 기준 — 레거시 r.currencyCode 는 어셈블러가
                  KRW 하드코딩(외화 결제에도)이라 신뢰하지 않는다. */}
              {formatPaymentMinorAmount(
                r.amount,
                currencyCode,
                (r as RefundDetailExtended).chargeAmount,
              )}
            </TableCell>
            <TableCell className="text-[color:var(--content-default)]">
              {(r as RefundDetailExtended).manual ? (
                <span
                  className="mr-2 rounded px-1.5 py-0.5 text-[11px]"
                  style={{ background: 'var(--status-neutral-bg)', color: 'var(--status-neutral-fg)' }}
                  data-testid="refund-manual-tag"
                >
                  {t('payments.manualRefund.badge', '수기 환불')}
                </span>
              ) : null}
              {labelOf(REFUND_REASON_LABELS, r.reason)}
              {r.detail ? (
                <span className="ml-2 text-[color:var(--content-muted)]">{r.detail}</span>
              ) : null}
              {(() => {
                // 수기 환불: 방법 · 처리자 · 처리일 · 계좌(마스킹). PG 환불이면 manual 이 없다.
                const m = (r as RefundDetailExtended).manual;
                if (!m) return null;
                const method = m.method ?? 'OTHER';
                const account = [krBankName(m.bankCode) ?? m.bankCode, m.maskedAccountNumber, m.holderName]
                  .filter(Boolean)
                  .join(' ');
                const parts = [
                  t(`payments.manualRefund.methods.${method}`, { defaultValue: MANUAL_METHOD_FALLBACK[method] }),
                  m.processedBy,
                  m.processedAt ? formatDateTime(m.processedAt) : null,
                  account || null,
                ].filter(Boolean);
                return (
                  <div className="mt-0.5 text-[11px] text-[color:var(--content-muted)]" data-testid="refund-manual-detail">
                    {parts.join(' · ')}
                  </div>
                );
              })()}
            </TableCell>
            <TableCell>
              {labelOf(REFUND_STATUS_LABELS, r.refundStatus)}
            </TableCell>
            <TableCell className="tabular-nums text-[color:var(--content-muted)]">
              {formatDateTime(r.completedAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
